import { REPORT_SENTIMENT_ENABLED } from '../../../lib/report-entitlements';
import { TONE_CLASSES, toneAt } from '../analytics/chartTheme';

interface ThemeAnalysis {
  /** Stable authored identity, absent for inferred themes. */
  sectionId?: string;
  theme: string;
  percentage: number;
  avgSentiment: number;
  sentimentLabel: 'positive' | 'negative' | 'neutral';
  turnIndices: number[];
}

interface ThemeCardsProps {
  themes: ThemeAnalysis[];
  /** Optional custom label formatter. Default: `${percentage}% of conversation` */
  labelFormatter?: (theme: ThemeAnalysis) => string;
}

function getSentimentColor(sentimentLabel: 'positive' | 'negative' | 'neutral'): string {
  switch (sentimentLabel) {
    case 'positive':
      return '#34D399';
    case 'negative':
      return '#F87171';
    case 'neutral':
    default:
      return 'var(--color-muted-foreground)';
  }
}

function formatSentimentScore(avgSentiment: number): string {
  const sign = avgSentiment >= 0 ? '+' : '';
  return `${sign}${avgSentiment.toFixed(1)}`;
}

/** Themes as toned cards with a share bar, matching the Overview's card design. */
export function ThemeCards({ themes, labelFormatter }: ThemeCardsProps) {
  if (!themes.length) return null;
  return (
    <section aria-label="Themes" className="flex flex-col gap-4">
      <h2 className="text-base font-semibold text-foreground">Themes</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {themes.map((theme, index) => {
          const color = REPORT_SENTIMENT_ENABLED ? getSentimentColor(theme.sentimentLabel) : undefined;
          return (
            <article key={index} className={`${TONE_CLASSES[toneAt(index)]} flex flex-col gap-3 rounded-[20px] p-6`}>
              <p className="text-[15px] font-medium text-foreground">{theme.theme}</p>
              <span className="block h-3 w-full overflow-hidden rounded-full bg-[var(--chart-track)]">
                <span className="block h-full rounded-full bg-[var(--chart-mark)]" style={{ width: `${Math.max(0, Math.min(100, theme.percentage))}%` }} />
              </span>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{labelFormatter ? labelFormatter(theme) : `${Math.round(theme.percentage)}% of conversation`}</span>
                {REPORT_SENTIMENT_ENABLED && <span className="flex items-center gap-1.5">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
                  {formatSentimentScore(theme.avgSentiment)}
                </span>}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default ThemeCards;
